/**
 * TransformNode - Applies position/rotation/scale via Three.js Group
 *
 * Declaration node: holds transform params, provides applyTransform() for
 * SceneNode to call. SceneNode creates a Group and TransformNode sets
 * position/rotation/scale on it. On param change, mutates the Group directly.
 * No geometry cloning — Three.js applies the transform natively during render.
 *
 * A single TransformNode may be discovered by multiple Scenes (e.g. a building's
 * own MeshViewer and an outer selector's MeshViewer). We store all Groups so
 * param changes update every scene that references this transform.
 */

import { BaseNode } from '@nodes/BaseNode';
import { internals, params } from '@/nodes/TypedEndpoints';
import type { NodeDef } from '@runtime/IRuntime';
import type { ThreeDependencies } from '../threejsPlugin';
import { logger } from '@/logging';

const transformEndpoints = {
  ...internals({
    in: { dataType: 'three:group|three:geometry', isInput: true, label: 'In' },
    out: { dataType: 'three:group|three:geometry', isOutput: true }
  }),
  ...params({
    posX: { value: 0, min: -100, max: 100, step: 0.01, group: 'Position' },
    posY: { value: 0, min: -100, max: 100, step: 0.01, group: 'Position' },
    posZ: { value: 0, min: -100, max: 100, step: 0.01, group: 'Position' },
    rotX: { value: 0, min: -Math.PI, max: Math.PI, step: 0.01, group: 'Rotation' },
    rotY: { value: 0, min: -Math.PI, max: Math.PI, step: 0.01, group: 'Rotation' },
    rotZ: { value: 0, min: -Math.PI, max: Math.PI, step: 0.01, group: 'Rotation' },
    scaleX: { value: 1, min: 0.01, max: 10, step: 0.01, group: 'Scale' },
    scaleY: { value: 1, min: 0.01, max: 10, step: 0.01, group: 'Scale' },
    scaleZ: { value: 1, min: 0.01, max: 10, step: 0.01, group: 'Scale' }
  })
};

class TransformNode extends BaseNode<typeof transformEndpoints> {
  /** All Group references from Scene realizations — multiple Scenes may discover us */
  private groups = new Set<any>();
  /** All rebuild callbacks from Scene realizations */
  private rebuildCallbacks = new Set<() => void>();
  private animateTraceCount = 0;

  /** Per-frame: re-evaluate expression params and apply transform to all stored Groups */
  animate(): void {
    this.pruneStaleGroups();
    // Log first 3 frames for repeater children to confirm values arrive
    if (this.animateTraceCount < 3 && this.deps.instancePath.includes('~')) {
      const px = this.params.posX.value;
      const pz = this.params.posZ.value;
      logger.trace(`[TransformNode.animate] ${this.deps.instancePath}/${this.id} posX=${px} posZ=${pz} groups=${this.groups.size}`);
      this.animateTraceCount++;
    }
    for (const group of this.groups) {
      this.applyTransform(group);
    }
  }

  constructor(id: string, deps: ThreeDependencies) {
    super(id, deps);
  }

  /** Apply current transform params to a Group — called by SceneNode */
  applyTransform(group: any): void {
    const px = this.params.posX.value;
    const pz = this.params.posZ.value;
    group.position.set(
      px,
      this.params.posY.value,
      pz
    );
    group.rotation.set(
      this.params.rotX.value,
      this.params.rotY.value,
      this.params.rotZ.value
    );
    group.scale.set(
      this.params.scaleX.value,
      this.params.scaleY.value,
      this.params.scaleZ.value
    );
  }

  /** Store Group reference from SceneNode for in-place param mutation */
  setThreeObject(obj: any): void {
    // Only store Groups (for in-place mutation). When transforms are baked
    // into geometry (e.g. upstream of Boolean), we receive a Mesh instead —
    // ignore it and rely on rebuildCallbacks for updates.
    if (obj?.isGroup) {
      this.groups.add(obj);
      if (this.deps.instancePath.includes('~')) {
        logger.trace(`[TransformNode.setThreeObject] ${this.deps.instancePath}/${this.id} stored group (total=${this.groups.size})`);
      }
    }
  }

  /** Remove a previously stored Group (called during scene cleanup) */
  removeThreeObject(obj: any): void {
    this.groups.delete(obj);
  }

  /** Store rebuild callback from SceneNode */
  setRebuildCallback(callback: () => void): void {
    this.rebuildCallbacks.add(callback);
  }

  /** Remove a previously stored rebuild callback (called during scene cleanup) */
  removeRebuildCallback(callback: () => void): void {
    this.rebuildCallbacks.delete(callback);
  }

  onInternalChanged(): void {
    for (const cb of this.rebuildCallbacks) cb();
  }

  onParamChanged(payload: any, endpointId: string): void {
    if ((endpointId === 'posX' || endpointId === 'posZ') && this.deps.instancePath.includes('~')) {
      const raw = payload && typeof payload === 'object' && 'value' in payload ? payload.value : payload;
      logger.trace(`[TransformNode.onParamChanged] ${this.deps.instancePath}/${this.id} ${endpointId}=${raw} groups=${this.groups.size}`);
    }
    this.pruneStaleGroups();
    if (this.groups.size > 0) {
      for (const group of this.groups) {
        this.applyTransform(group);
      }
    } else {
      // No groups — transforms are baked into geometry (e.g. upstream of Boolean).
      // Trigger geometry refresh so the pipeline re-bakes with current params.
      for (const cb of this.rebuildCallbacks) cb();
    }
  }

  /** Remove groups no longer attached to a scene (parent removed during clearScene) */
  private pruneStaleGroups(): void {
    for (const group of this.groups) {
      if (!group.parent) this.groups.delete(group);
    }
  }

  dispose(): void {
    this.groups.clear();
    this.rebuildCallbacks.clear();
    super.dispose();
  }
}

export const transformNodeDef: NodeDef<TransformNode> = {
  type: 'Transform',
  shortName: 'transform',
  info: 'Applies position, rotation, and scale to child geometry via a Three.js Group.',
  category: 'transform',
  endpoints: transformEndpoints,
  ctor: TransformNode
};
